import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Tiny first-time coach-mark tour. Zero third-party deps.
 *
 * Each step targets a DOM element by its `data-tour-id` attribute and
 * shows a popover card next to it with a label + description + Next/Skip
 * buttons. Position is recomputed on viewport resize and on scroll so the
 * popover sticks to its anchor reliably.
 *
 * "Seen" state is stored under the caller-provided localStorage key, so
 * the tour appears once per device, never again — unless the key is
 * cleared. Skip and complete both flip the same flag.
 *
 * No animation on the page underneath. No scrollLocking. A polite, calm,
 * skippable tour that gets out of the user's way.
 */

export type TourStep = {
  /** `data-tour-id` value of the element to anchor to. */
  targetId: string;
  /** Step title — keep under 5 words. */
  title: string;
  /** Body copy — one or two sentences, plain English. */
  body: string;
  /**
   * Side to prefer when placing the popover. Falls back to the opposite
   * side if the preferred side has no room.
   */
  side?: "top" | "right" | "bottom" | "left";
};

type Props = {
  steps: TourStep[];
  /** localStorage key used to remember "seen" state. */
  storageKey: string;
};

const POPOVER_W = 320;
const POPOVER_OFFSET = 14;

export function Tour({ steps, storageKey }: Props) {
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // On mount, decide whether to show. Defer to an effect so we don't block
  // first paint and SSR mismatch concerns evaporate.
  useEffect(() => {
    try {
      if (window.localStorage.getItem(storageKey) === "1") return;
    } catch {
      return;
    }
    // Give the page one tick to mount its `data-tour-id` anchors.
    const t = window.setTimeout(() => setActive(true), 60);
    return () => window.clearTimeout(t);
  }, [storageKey]);

  // Track the current anchor's bounding rect. Re-measure on step change,
  // scroll, and resize. ResizeObserver would be overkill — these events
  // catch every realistic case.
  const measure = useCallback(() => {
    if (!active) return;
    const step = steps[stepIndex];
    if (!step) return;
    const el = document.querySelector<HTMLElement>(`[data-tour-id="${step.targetId}"]`);
    if (!el) {
      setAnchorRect(null);
      return;
    }
    setAnchorRect(el.getBoundingClientRect());
  }, [active, stepIndex, steps]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    if (!active) return;
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [active, measure]);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(storageKey, "1");
    } catch {
      /* persist failed — at least hide for this session */
    }
    setActive(false);
  }, [storageKey]);

  const next = useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      dismiss();
    } else {
      setStepIndex((i) => i + 1);
    }
  }, [stepIndex, steps.length, dismiss]);

  // Keyboard: Esc to skip, Enter / → to advance. Doesn't trap focus —
  // input on the page should still be reachable.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
      else if (e.key === "Enter" || e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, dismiss, next]);

  if (!active) return null;
  const step = steps[stepIndex];
  if (!step) return null;

  const placement = computePlacement(anchorRect, step.side ?? "right");

  return createPortal(
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-0 z-[70]"
    >
      {anchorRect && (
        <div
          aria-hidden
          className="pointer-events-none absolute rounded-md"
          style={{
            top: anchorRect.top - 4,
            left: anchorRect.left - 4,
            width: anchorRect.width + 8,
            height: anchorRect.height + 8,
            boxShadow:
              "0 0 0 2px var(--studio, #0072CE), 0 0 0 9999px rgba(33,43,50,0.18)",
            transition: "all 220ms ease",
          }}
        />
      )}
      <div
        ref={popoverRef}
        role="dialog"
        aria-label={step.title}
        className="pointer-events-auto absolute rounded-lg border border-[var(--border-1)] bg-white p-4 shadow-lg"
        style={{
          top: placement.top,
          left: placement.left,
          width: POPOVER_W,
          transition: "top 220ms ease, left 220ms ease",
        }}
      >
        <div className="mb-1 flex items-center gap-2 text-eyebrow font-semibold uppercase text-fg-mute">
          <span
            aria-hidden
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ background: "var(--studio)" }}
          />
          Step {stepIndex + 1} of {steps.length}
        </div>
        <h3 className="m-0 mb-1.5 text-base font-semibold text-fg-1">{step.title}</h3>
        <p className="m-0 mb-4 text-sm leading-relaxed text-fg-2">{step.body}</p>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={dismiss}
            className="text-xs font-medium text-fg-3 hover:text-fg-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] rounded"
          >
            Skip tour
          </button>
          <button
            type="button"
            onClick={next}
            className="rounded-md px-3 py-1.5 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
            style={{ background: "var(--studio)" }}
          >
            {stepIndex >= steps.length - 1 ? "Got it" : "Next"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Place the popover next to the anchor, preferring the requested side.
 * Falls back to the opposite side if there isn't room; clamps to the
 * viewport so the card never clips off-screen.
 */
function computePlacement(
  rect: DOMRect | null,
  preferred: "top" | "right" | "bottom" | "left",
): { top: number; left: number } {
  // No anchor (e.g. element not mounted yet) — float the card in the
  // top-right so the user can still read it and skip.
  if (!rect) {
    return { top: 24, left: Math.max(24, window.innerWidth - POPOVER_W - 24) };
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Approximate the card's height for vertical clamping. The real height
  // is unknown until render, but 180 px is a safe upper bound for the
  // step content here.
  const POPOVER_H = 180;

  const tryPlace = (side: typeof preferred) => {
    switch (side) {
      case "right":
        return {
          top: rect.top + rect.height / 2 - POPOVER_H / 2,
          left: rect.right + POPOVER_OFFSET,
        };
      case "left":
        return {
          top: rect.top + rect.height / 2 - POPOVER_H / 2,
          left: rect.left - POPOVER_OFFSET - POPOVER_W,
        };
      case "top":
        return {
          top: rect.top - POPOVER_OFFSET - POPOVER_H,
          left: rect.left + rect.width / 2 - POPOVER_W / 2,
        };
      case "bottom":
      default:
        return {
          top: rect.bottom + POPOVER_OFFSET,
          left: rect.left + rect.width / 2 - POPOVER_W / 2,
        };
    }
  };

  const fits = (p: { top: number; left: number }) =>
    p.left >= 8 && p.left + POPOVER_W <= vw - 8 && p.top >= 8 && p.top + POPOVER_H <= vh - 8;

  let placement = tryPlace(preferred);
  if (!fits(placement)) {
    const opposite =
      preferred === "right" ? "left" :
      preferred === "left"  ? "right" :
      preferred === "top"   ? "bottom" : "top";
    const alt = tryPlace(opposite);
    if (fits(alt)) placement = alt;
  }

  // Final clamp — keep on-screen even if no side fits cleanly.
  return {
    top: Math.min(Math.max(8, placement.top), vh - POPOVER_H - 8),
    left: Math.min(Math.max(8, placement.left), vw - POPOVER_W - 8),
  };
}
